import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/services/supabase-client.service';

type CareApprovalAccess = {
    role: string | null;
    qualification: string | null;
    canApprove: boolean;
};

function hasApprovalAccess(role: string | null, qualification: string | null) {
    const value = `${role ?? ''} ${qualification ?? ''}`.toLowerCase();
    return ['arzt', 'doctor', 'supervisor', 'facility_admin', 'admin', 'pdl', 'leitung'].some((token) => value.includes(token));
}

export function useWoundApprovalAccess() {
    const { user } = useAuth();

    return useQuery<CareApprovalAccess>({
        queryKey: ['wound-approval-access', user?.careStaffId],
        queryFn: async () => {
            if (!user?.careStaffId) {
                return { role: null, qualification: null, canApprove: false };
            }

            const { data, error } = await supabase
                .from('care_staff')
                .select('role, qualification')
                .eq('id', user.careStaffId)
                .eq('is_active', true)
                .maybeSingle();

            if (error) {
                throw new Error(error.message);
            }

            const role = (data?.role as string | undefined) ?? null;
            const qualification = (data?.qualification as string | undefined) ?? null;

            return {
                role,
                qualification,
                canApprove: hasApprovalAccess(role, qualification),
            };
        },
        enabled: !!user?.careStaffId,
        staleTime: 300_000,
    });
}